/**
 * Cache busting utilities for handling stale resource issues
 */

export interface CacheBustConfig {
  clearWebpackCache: boolean;
  clearBrowserCache: boolean;
  clearStorage: boolean;
  addTimestamp: boolean;
  reload: boolean;
}

const defaultConfig: CacheBustConfig = {
  clearWebpackCache: true,
  clearBrowserCache: true,
  clearStorage: false,
  addTimestamp: true,
  reload: false,
};

/**
 * Clear various types of caches to force fresh resource loading
 */
export async function clearCaches(config: Partial<CacheBustConfig> = {}): Promise<void> {
  const finalConfig = { ...defaultConfig, ...config };

  const promises: Promise<void>[] = [];

  // Clear webpack module cache
  if (finalConfig.clearWebpackCache) {
    promises.push(clearWebpackCache());
  }

  // Clear browser caches
  if (finalConfig.clearBrowserCache) {
    promises.push(clearBrowserCache());
  }

  // Clear storage
  if (finalConfig.clearStorage) {
    promises.push(clearStorage());
  }

  await Promise.allSettled(promises);

  // Reload page if requested
  if (finalConfig.reload) {
    reloadWithCacheBust();
  }
}

/**
 * Clear webpack's internal module cache
 */
export async function clearWebpackCache(): Promise<void> {
  try {
    if (typeof window !== 'undefined' && (window as any).__webpack_require__) {
      const webpackRequire = (window as any).__webpack_require__;

      // Clear module cache
      if (webpackRequire.cache) {
        Object.keys(webpackRequire.cache).forEach(key => {
          delete webpackRequire.cache[key];
        });
      }

      // Clear chunk cache if available
      if (webpackRequire.installedChunks) {
        Object.keys(webpackRequire.installedChunks).forEach(chunkId => {
          if (chunkId !== 'runtime') {
            delete webpackRequire.installedChunks[chunkId];
          }
        });
      }

      console.log('Webpack cache cleared');
    }
  } catch (error) {
    console.warn('Failed to clear webpack cache:', error);
  }
}

/**
 * Clear browser caches using Cache API
 */
export async function clearBrowserCache(): Promise<void> {
  try {
    if ('caches' in window) {
      const cacheNames = await caches.keys();

      const deletePromises = cacheNames.map(async cacheName => {
        try {
          const success = await caches.delete(cacheName);
          if (success) {
            console.log(`Cleared cache: ${cacheName}`);
          }
        } catch (error) {
          console.warn(`Failed to clear cache ${cacheName}:`, error);
        }
      });

      await Promise.allSettled(deletePromises);
      console.log('Browser caches cleared');
    }
  } catch (error) {
    console.warn('Failed to clear browser cache:', error);
  }
}

/**
 * Clear localStorage and sessionStorage items related to chunks/webpack
 */
export async function clearStorage(): Promise<void> {
  try {
    const keysToRemove: string[] = [];

    // Find keys related to webpack/chunks in localStorage
    Object.keys(localStorage).forEach(key => {
      if (key.includes('webpack') || key.includes('chunk') || key.includes('_cache')) {
        keysToRemove.push(key);
      }
    });

    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
    });

    // Find keys related to webpack/chunks in sessionStorage
    const sessionKeysToRemove: string[] = [];
    Object.keys(sessionStorage).forEach(key => {
      if (key.includes('webpack') || key.includes('chunk') || key.includes('_cache')) {
        sessionKeysToRemove.push(key);
      }
    });

    sessionKeysToRemove.forEach(key => {
      sessionStorage.removeItem(key);
    });

    if (keysToRemove.length > 0 || sessionKeysToRemove.length > 0) {
      console.log('Storage cleared:', { localStorage: keysToRemove, sessionStorage: sessionKeysToRemove });
    }
  } catch (error) {
    console.warn('Failed to clear storage:', error);
  }
}

/**
 * Reload the page with cache-busting parameters
 */
export function reloadWithCacheBust(): void {
  try {
    const url = new URL(window.location.href);
    url.searchParams.set('_cb', Date.now().toString());
    url.searchParams.set('_refresh', '1');
    window.location.href = url.toString();
  } catch (error) {
    console.warn('Failed to reload with cache bust, falling back to simple reload:', error);
    window.location.reload();
  }
}

/**
 * Create a cache-busted URL for a resource
 */
export function addCacheBustToUrl(url: string): string {
  try {
    const urlObj = new URL(url, window.location.origin);
    urlObj.searchParams.set('_cb', Date.now().toString());
    return urlObj.toString();
  } catch (error) {
    console.warn('Failed to add cache bust to URL:', error);
    return url;
  }
}

/**
 * Wrap fetch to add cache-busting for specific resource types
 */
export function createCacheBustingFetch(
  originalFetch = window.fetch,
  resourceTypes: string[] = ['.js', '.css', 'chunk'],
): typeof fetch {
  return function cacheBustingFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
    if (typeof input === 'string') {
      const shouldCacheBust = resourceTypes.some(type => input.includes(type));
      if (shouldCacheBust) {
        return originalFetch(addCacheBustToUrl(input), init);
      }
    }
    return originalFetch(input, init);
  };
}

/**
 * Temporarily replace fetch with cache-busting version
 */
export function withCacheBustedFetch<T>(fn: () => Promise<T>): Promise<T> {
  const originalFetch = window.fetch;
  const cacheBustedFetch = createCacheBustingFetch(originalFetch);

  window.fetch = cacheBustedFetch;

  return fn().finally(() => {
    window.fetch = originalFetch;
  });
}

/**
 * Check if current page load appears to have cache issues
 */
export function detectCacheIssues(): { hasIssues: boolean; indicators: string[] } {
  const indicators: string[] = [];

  // Check URL parameters for cache bust indicators
  const url = new URL(window.location.href);
  if (url.searchParams.has('_cb') || url.searchParams.has('_refresh')) {
    indicators.push('Cache bust parameters present');
  }

  // Check for webpack errors in console
  const hasWebpackErrors = performance
    .getEntriesByType('navigation')
    .some(entry => (entry as PerformanceNavigationTiming).loadEventEnd === 0);

  if (hasWebpackErrors) {
    indicators.push('Navigation timing suggests loading issues');
  }

  // Check for failed resource loads
  const failedResources = performance
    .getEntriesByType('resource')
    .filter(entry => (entry as PerformanceResourceTiming).transferSize === 0)
    .filter(entry => entry.name.includes('.js') || entry.name.includes('chunk'));

  if (failedResources.length > 0) {
    indicators.push(`${failedResources.length} failed resource loads detected`);
  }

  return {
    hasIssues: indicators.length > 0,
    indicators,
  };
}

/**
 * Emergency cache clear and reload for critical failures
 */
export async function emergencyReload(): Promise<void> {
  console.warn('Performing emergency cache clear and reload');

  // Clear everything
  await clearCaches({
    clearWebpackCache: true,
    clearBrowserCache: true,
    clearStorage: true,
    addTimestamp: true,
    reload: false, // We'll do this manually
  });

  // Force reload with maximum cache busting
  const url = new URL(window.location.href);
  url.searchParams.set('_emergency', Date.now().toString());
  url.searchParams.set('_clear_cache', '1');

  // Use replace to prevent back button issues
  window.location.replace(url.toString());
}

/**
 * Initialize cache management with automatic issue detection
 */
export function initCacheManagement(): void {
  if (typeof window === 'undefined') return;

  // Check for cache issues on load
  window.addEventListener('load', () => {
    setTimeout(() => {
      const { hasIssues, indicators } = detectCacheIssues();
      if (hasIssues) {
        console.log('Cache issues detected:', indicators);
      }
    }, 1000);
  });

  // Listen for chunk loading errors
  window.addEventListener('error', event => {
    if (event.error?.name === 'ChunkLoadError') {
      console.warn('ChunkLoadError detected, cache management will handle recovery');
    }
  });

  console.log('Cache management initialized');
}
